import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Search, Download, Shield, User, ChevronRight, Trash2, CreditCard, Plus,
} from "lucide-react";

const AdminUsers = ({
  users, usersTotal, searchQuery, setSearchQuery, loadUsers,
  handleExportCSV, selectedUser, setSelectedUser,
  showUserDialog, setShowUserDialog,
  handleUpdateRole, handleToggleSub, handleExtendTrial, handleDeleteUser,
}) => (
  <div className="space-y-4" data-testid="admin-users-tab">
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px] relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search by name or email..."
          className="bg-white/5 border-white/10 text-white pl-10 h-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadUsers(searchQuery)}
          data-testid="admin-user-search"
        />
      </div>
      <Button onClick={() => loadUsers(searchQuery)} className="bg-violet-500/20 text-violet-300 border border-violet-500/30 hover:bg-violet-500/30">
        <Search className="w-4 h-4 mr-1" /> Search
      </Button>
      <Button onClick={handleExportCSV} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="export-csv-btn">
        <Download className="w-4 h-4 mr-1" /> Export CSV
      </Button>
    </div>

    <p className="text-xs text-slate-500">{usersTotal} users found</p>

    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.user_id}
          className="bg-slate-900/70 rounded-xl border border-white/5 p-4 flex items-center justify-between cursor-pointer hover:border-violet-500/20 transition-colors"
          onClick={() => { setSelectedUser(u); setShowUserDialog(true); }}
          data-testid={`user-row-${u.user_id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${u.role === "admin" ? "bg-violet-500/20" : "bg-slate-800"}`}>
              {u.role === "admin" ? <Shield className="w-4 h-4 text-violet-400" /> : <User className="w-4 h-4 text-slate-400" />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{u.name || u.email}</p>
              <p className="text-xs text-slate-500 truncate">
                {u.email} · {u.subscription === "paid" ? <span className="text-amber-400">Paid</span> : "Free"} · Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "?"}
              </p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
        </div>
      ))}
    </div>

    {/* User Detail Dialog */}
    <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
      <DialogContent className="bg-[#141c2e] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle>User Details</DialogTitle></DialogHeader>
        {selectedUser && (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-[11px] text-slate-500">Name</p><p className="text-sm text-white">{selectedUser.name || "Not set"}</p></div>
              <div><p className="text-[11px] text-slate-500">Email</p><p className="text-sm text-white truncate">{selectedUser.email}</p></div>
              <div><p className="text-[11px] text-slate-500">Registered</p><p className="text-sm text-white">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : "?"}</p></div>
              <div><p className="text-[11px] text-slate-500">Last Login</p><p className="text-sm text-white">{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleDateString() : "Never"}</p></div>
              <div><p className="text-[11px] text-slate-500">Home Country</p><p className="text-sm text-white">{selectedUser.home_country || "Not set"}</p></div>
              <div><p className="text-[11px] text-slate-500">User ID</p><p className="text-sm text-white font-mono text-xs">{selectedUser.user_id}</p></div>
            </div>

            <div className="border-t border-white/8 pt-4 space-y-3">
              <div>
                <p className="text-[11px] text-slate-500 mb-1">Role</p>
                <Select value={selectedUser.role || "user"} onValueChange={(v) => handleUpdateRole(selectedUser.user_id, v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a2440] border-white/10 text-white">
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-[11px] text-slate-500 mb-1">Subscription</p>
                <div className="flex gap-2">
                  <Button size="sm" className={`flex-1 h-8 text-xs ${selectedUser.subscription === "paid" ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" : "bg-white/5 text-slate-400 border border-white/10"}`}
                    onClick={() => handleToggleSub(selectedUser.user_id, "paid")}><CreditCard className="w-3 h-3 mr-1" /> Paid</Button>
                  <Button size="sm" className={`flex-1 h-8 text-xs ${selectedUser.subscription !== "paid" ? "bg-slate-500/20 text-slate-300 border border-slate-500/30" : "bg-white/5 text-slate-400 border border-white/10"}`}
                    onClick={() => handleToggleSub(selectedUser.user_id, "free")}>Free</Button>
                </div>
              </div>
              <Button size="sm" variant="outline" className="w-full h-8 text-xs border-sky-500/30 text-sky-400 hover:bg-sky-500/10"
                onClick={() => handleExtendTrial(selectedUser.user_id)}><Plus className="w-3 h-3 mr-1" /> Extend Trial +14 days</Button>
            </div>

            <div className="border-t border-white/8 pt-3">
              <Button variant="outline" className="w-full text-red-400 border-red-500/30 hover:bg-red-500/10 h-9 text-sm"
                onClick={() => handleDeleteUser(selectedUser.user_id)}><Trash2 className="w-4 h-4 mr-2" /> Delete User & All Data</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </div>
);

export default AdminUsers;
